/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import { type VehicleInstallGuideResponse, type VehicleInstallGuideOptions } from '../interface';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/vehicle-install-guides`;

const getVehicleInstallGuide = async (
  options: VehicleInstallGuideOptions,
  axiosInstance?: AxiosInstance,
): Promise<VehicleInstallGuideResponse> => {
  const response = await getAxios(axiosInstance).get<VehicleInstallGuideResponse>(URL, {
    params: options,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  const data = response.data;

  const urlKey = 'imageURL';

  /** TODO: Needs to be refined when /product-vehicle-image is deprecated  */
  const imagesArr = data.images
    ? data.images.map((item) => {
        const { imageUrl, imageURL, description } = item;
        return { description, [urlKey]: imageUrl || imageURL || '' };
      })
    : [];

  return {
    ...data,
    images: imagesArr ?? [],
    videos: data.videos ?? [],
  };
};

const { useData: useVehicleInstallGuideInfo, prefetch: prefetchVehicleInstallGuideInfo } =
  createQuery<VehicleInstallGuideResponse, VehicleInstallGuideOptions>(
    'getVehicleInstallGuide',
    async (options, axiosInstance) => getVehicleInstallGuide({ ...options }, axiosInstance),
  );

export const useGetVehicleInstallGuide = (props: {
  country: string;
  year: string;
  makeName: string;
  modelName: string;
  engineName: string;
  fuelTypeId: number;
  location: string;
  enabled: boolean;
}) => {
  const { country, year, makeName, modelName, engineName, location, fuelTypeId, enabled } = props;

  const options = {
    country,
    year,
    makeName,
    modelName,
    engineName,
    fuelTypeId,
    location,
    enabled,
  };

  return useVehicleInstallGuideInfo(options);
};

export const prefetchVehicleInstallGuide = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: VehicleInstallGuideOptions,
) => {
  return prefetchVehicleInstallGuideInfo(queryClient, options, axiosInstance);
};
